import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FAQ {
  q: string;
  a: string;
}

const ChevronIcon = ({ size = 18, open = false }: { size?: number; open?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const FAQSection = React.memo(() => {
  const { t } = useTranslation();
  const [openIdx, setOpenIdx] = useState<number>(0);
  const faqs = t('landing.faq.items', { returnObjects: true }) as FAQ[];

  return (
    <section className="py-24 px-6 bg-gray-50/60 border-y border-gray-100">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">{t('landing.faq.eyebrow')}</span>
          <h2
            className="text-3xl md:text-5xl font-medium mt-3 mb-5"
            style={{
              background: 'linear-gradient(to bottom, #000000, #000000, rgba(0, 0, 0, 0.6))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            {t('landing.faq.title')}
          </h2>
          <p className="text-base text-gray-500">
            {t('landing.faq.subtitlePrefix')}{' '}
            <a href="#contato" className="text-black underline underline-offset-2 hover:no-underline">{t('landing.faq.subtitleLink')}</a>.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={item.q}
                className={`rounded-xl border transition-all ${isOpen ? 'bg-white border-gray-300 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300'}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm md:text-base font-medium text-black">{item.q}</span>
                  <ChevronIcon size={18} open={isOpen} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 -mt-1">
                    <p className="text-sm leading-relaxed text-gray-600">{item.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});
FAQSection.displayName = 'FAQSection';
